import { HIMMELSRICHTUNGEN } from "@dj/domain/lage";
import { berlinUhrzeit } from "@dj/domain/gemeinsam";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Textfeld } from "../design/Formular";
import { Schutzflaeche } from "../design/Hinweis";
import { Kachel } from "../design/Kachel";
import { fensterOffen, useRueckgaengig } from "../design/Rueckgaengig";
import { useSperre } from "../design/sperre";
import { Symbol } from "../design/Symbol";
import { WILDARTEN_IM_ABLAUF } from "../design/wildarten";
import { positionMessen, type Messung } from "../plattform/position";
import { serverkontakt } from "../plattform/serverzeit";
import { positionText, useMeineAufgaben, type Rahmen } from "./daten";
import { mitGenauigkeit, mitPosition } from "./meldungen";

/**
 * Ablauf „Wildsichtung“ (oberflaeche.md §5, F-130; design/README §9.6, K71): Wildart → Anzahl („1“ … „9“; „mehr“ öffnet
 * für größere Rotten die Ansicht „Stückzahl“ mit Feld, K88 a) → [Bewegungsrichtung]. Die Anzahl meldet sofort
 * (Rückgängig-Fenster, ADR-0023); die Bewegungsrichtung
 * ist optional und ergänzt die noch offene Meldung (3 × 3, Nord oben, design/README §9.7). Die Position misst das Gerät
 * beim Öffnen des Ablaufs; ohne Position wird trotzdem gemeldet (LIM-0009).
 */
type Schritt = "wildart" | "anzahl" | "stueckzahl" | "richtung";

const SYMBOL: Record<string, string> = { Raubwild: "raubwild", Rehwild: "rehwild", Damwild: "damwild", Rotwild: "rotwild", Schwarzwild: "schwarzwild" };
/** 3 × 3 der Bewegungsrichtung, Nord oben; `null` = Mitte (Angabe der Sichtung) */
const RASTER: Array<string | null> = ["NW", "N", "NO", "W", null, "O", "SW", "S", "SO"];
const ZAHLEN = [1, 2, 3, 4, 5, 6, 7, 8, 9];

export function WildsichtungAblauf({ rahmen }: { rahmen: Rahmen }) {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const rg = useRueckgaengig();
  const [schritt, setSchritt] = useState<Schritt>("wildart");
  const [wildart, setWildart] = useState<string | null>(null);
  const [stueckzahl, setStueckzahl] = useState("");
  const [gemessen, setGemessen] = useState<Messung | null>(null);
  const aufgaben = useMeineAufgaben(id);
  const [gemeldet, setGemeldet] = useState<{ meldungsId: string; anzahl: number } | null>(null);
  const messung = useRef<Promise<Messung | null> | null>(null);
  const gesperrt = useSperre("ablauf", schritt);
  useEffect(() => {
    let aktiv = true;
    messung.current = positionMessen();
    void messung.current.then((m) => { if (aktiv) setGemessen(m); });
    return () => { aktiv = false; };
  }, []);

  const zurStartansicht = () => navigate(`/drueckjagd/${id}`, { replace: true });

  // Ohne Bewegungsrichtung endet der Ablauf mit dem Rückgängig-Fenster der Meldung (wie die Fluchtrichtung beim Schuss).
  const fensterGesehen = useRef(false);
  useEffect(() => {
    if (schritt !== "richtung" || !gemeldet) return;
    if (fensterOffen(rg.offen, gemeldet.meldungsId)) fensterGesehen.current = true;
    else if (fensterGesehen.current) zurStartansicht();
  }, [schritt, gemeldet, rg.offen]);

  const melden = (anzahl: number) => {
    if (!wildart) return;
    const u = mitPosition(rg, {
      befehl: "WildsichtungMelden", begriff: "Wildsichtung", symbol: "wildsichtung", angaben: [[SYMBOL[wildart] ?? "andere-wildart", wildart], ["wildsichtung", `${anzahl} Stück`]],
      titel: (x) => `Wildsichtung ${x.meldungsId}`, daten: { "Drückjagd": id, Wildart: wildart, Anzahl: anzahl },
    }, mitGenauigkeit, messung.current ?? positionMessen());
    fensterGesehen.current = false;
    setGemeldet({ meldungsId: u.meldungsId, anzahl });
    setSchritt("richtung");
  };

  const richtung = (r: string) => {
    if (gemeldet) rg.ergaenzen(gemeldet.meldungsId, { Bewegungsrichtung: r },
      [[SYMBOL[wildart ?? ""] ?? "andere-wildart", wildart ?? ""], ["wildsichtung", `${gemeldet.anzahl} Stück`], ["fluchtrichtung", r]]);
    zurStartansicht();
  };

  const zurueck = () => {
    if (schritt === "wildart") zurStartansicht();
    else if (schritt === "anzahl") setSchritt("wildart");
    else if (schritt === "stueckzahl") setSchritt("anzahl");
    else {
      if (gemeldet && rg.offen?.umschlag.meldungsId === gemeldet.meldungsId) rg.zuruecknehmen();
      setGemeldet(null);
      setSchritt("anzahl");
    }
  };

  const nummer = schritt === "wildart" ? 1 : schritt === "anzahl" ? 2 : 3;
  // Ansicht „Stückzahl“ (K88 a, Referenz `#s-ws-zahl`, design/README §14 0f-3): Kopfansicht mit „Abbrechen“ oben im
  // Abdruck von „mehr“, darunter das Feld, Schutzfläche ohne Wildart, „Senden“ unten im Abdruck des 3 × 3 der Anzahl
  if (schritt === "stueckzahl" && wildart) {
    const zahl = /^\d+$/.test(stueckzahl.trim()) ? Number(stueckzahl.trim()) : null;
    const jetzt = berlinUhrzeit(new Date(Date.now() + serverkontakt().abweichung));
    return rahmen({
      drueckjagdId: id, name: "Stückzahl", art: "flaeche", titel: wildart, unter: "Wildsichtung", sperre: gesperrt, zurueck: zurueck,
      oben: (
        <>
          <div className="raster raster--2 liste__oben">
            <Kachel symbol="schliessen" begriff="Abbrechen" art="kompakt" onPress={() => setSchritt("anzahl")} />
          </div>
          <div className="liste__feld"><Textfeld label="Stückzahl" typ="number" eingabemodus="numeric" wert={stueckzahl} onChange={setStueckzahl} /></div>
        </>
      ),
      children: (
        <Schutzflaeche gross zeilen={[
          <b key="w">Wildsichtung</b>,
          [aufgaben.standnummer ? `Stand ${aufgaben.standnummer}` : null, jetzt].filter(Boolean).join(" · "),
          <>eigene Position · <span className="kennung">{positionText(gemessen?.position)}</span></>,
        ]} />
      ),
      unten: (
        <div className="raster raster--1 liste__unten">
          <Kachel symbol="senden" begriff="Senden" art="kompakt" onPress={() => { if (zahl !== null && zahl >= 1) melden(zahl); }} />
        </div>
      ),
    });
  }
  const angabe = (symbol: string, begriff: string, wert?: string) => (
    <div className="hinweis platte" data-zeile-fest="">
      <Symbol name={symbol} />
      <div><b>{begriff}</b>{wert ? <span>{wert}</span> : null}</div>
    </div>
  );
  return rahmen({
    drueckjagdId: id, name: "Wildsichtung", ablauf: schritt === "anzahl" ? "ablauf--ws" : schritt === "richtung" ? "ablauf--flucht" : true,
    titel: schritt === "wildart" ? "Wildart" : schritt === "anzahl" ? wildart ?? "Anzahl" : "Bewegungsrichtung",
    schritte: `Wildsichtung · ${nummer} von 3`, sperre: gesperrt,
    children: (
      <>
        <Kachel symbol="zurueck" begriff="Zurück" art="kompakt" className="zurueck-oben" onPress={zurueck} />
        {schritt === "wildart" ? (
          <>
            <div className="r12">
              <div className="raster raster--3x2" data-gleich="">
                {WILDARTEN_IM_ABLAUF.map((w) => <Kachel key={w} symbol={SYMBOL[w] ?? "andere-wildart"} begriff={w} art="wahl" fuge onPress={() => { setWildart(w); setSchritt("anzahl"); }} />)}
              </div>
            </div>
            <div className="r3">{angabe("wildsichtung", "Wildsichtung")}</div>
          </>
        ) : null}
        {schritt === "anzahl" && wildart ? (
          <>
            <div className="r12">
              {angabe(SYMBOL[wildart] ?? "andere-wildart", wildart)}
              <div className="raster raster--1"><Kachel symbol="mehr" begriff="mehr" art="kompakt" onPress={() => { setStueckzahl(""); setSchritt("stueckzahl"); }} /></div>
            </div>
            <div className="r3">
              <div className="raster raster--3x3" data-gleich="">
                {ZAHLEN.map((n) => (
                  <Kachel key={n} symbol="wildsichtung" begriff={String(n)} art="wahl" className="k--zahl" onPress={() => melden(n)} />
                ))}
              </div>
            </div>
          </>
        ) : null}
        {schritt === "richtung" && wildart ? (
          <>
            <div className="r12">{angabe(SYMBOL[wildart] ?? "andere-wildart", wildart, `${gemeldet?.anzahl ?? ""} Stück`)}</div>
            <div className="r3">
              <div className="raster raster--3x3">
                {RASTER.map((r) => (r && (HIMMELSRICHTUNGEN as readonly string[]).includes(r) ? (
                  <Kachel key={r} symbol="fluchtrichtung" begriff={r} art="wahl" className={`k--richtung k--richtung-${r}`} onPress={() => richtung(r)} />
                ) : (
                  <div key="mitte" className="flucht__mitte platte" data-zeile-fest=""><Symbol name="wildsichtung" /><span>{wildart}</span></div>
                )))}
              </div>
            </div>
          </>
        ) : null}
      </>
    ),
  });
}
